<script>
export default {
	name: 'MediaCentreSearch',
	inheritAttrs: false,
};
</script>

<script setup>
import { isMobileViewport } from '~/logic/composables/breakpoints.js';
import { useOffsetPagination } from '@vueuse/core';
import axios from 'axios';
import { useRouteQuery } from '@vueuse/router';
import { MHScrollToElement } from '~/logic/helpers/dom.js';
import BrandMonthYearFilter from '~/components/MediaCentreSearch/BrandMonthYearFilter.vue';
import MediaCentreSearchListing from '~/components/MediaCentreSearch/NewsListing.vue';
import MediaCentreSearchSearch from '~/components/MediaCentreSearch/Search.vue';
import MediaCentreListingNewsTile from '~/components/MediaCentreListing/NewsTile.vue';
import AppSpinner from '~/components/AppSpinner.vue';
import dayjs from 'dayjs';
import { useRouter, useRoute } from 'vue-router';
import { useGlobalAEMState } from '~/logic/aem/global-aem-state.js';
import { useIsRTL } from '~/logic/helpers/is-rtl.js';


const props = defineProps({
	searchPagePath: { type: String, default: '' },
	allLabel: { type: String, default: 'All' },
	applyCTAText: { type: String, default: 'Apply' },
	mobileFilterByLabel: { type: String, default: 'Filter by' },
	mobileResetCTAText: { type: String, default: 'Reset' },
	monthLabel: { type: String, default: 'Month' },
	yearLabel: { type: String, default: 'Year' },
	searchCTAText: { type: String, default: 'Search' },
	searchPlaceHolderText: { type: String, default: 'Search' },
	numberOfResults: { type: String, default: 'Showing {0} of {1} results' },
	previousButton: { type: String, default: 'Previous' },
	nextButton: { type: String, default: 'Next' },
	relativePath: { type: String, default: '' },
	noResultsMessage: { type: String, default: '' },
	crossSellLabel: { type: String, default: '' },
	featuredCrossSell: { type: Array, default: () => [] },
	mediaCentreSearchUrl: { type: String, default: '' },
	isHideInDesktop: { type: Boolean, default: false },
	isHideInMobile: { type: Boolean, default: false },
	hideMonth: { type: Boolean, default: false },
	enableBrandsDropdown: { type: Boolean, default: false },
	brandsLabel: { type: String, default: '' },
	brandsTags: { type: Array, default: () => [] },
});

const router = useRouter();
const route = useRoute();

const { isEditorMode } = useGlobalAEMState();

const contentEl = ref(null);
const isLoading = ref(true);
const apiErrorMsg = ref('');
const searchKeyword = ref('');

setTimeout(() => {
	isLoading.value = false;
}, 2500);

const searchResults = ref([]);
const yearMonthList = ref(new Set());
const yearKeyword = ref(useRouteQuery('year').value ? useRouteQuery('year').value : 'all');
const brandKeyword = ref(useRouteQuery('brand').value ? useRouteQuery('brand').value : 'all');

const getExistingMonthQuery = () => {
	if (useRouteQuery('month').value) {
		let listOfMonths = [...dayjs.months().map((mName, index) => {
			return {
				month: mName,
				value: dayjs().set('month', (index)).format('MM'),
			};
		})];
		let monthNumber = listOfMonths.find((data) => {
			return data.month == useRouteQuery('month').value;
		});
		return monthNumber.value;
	}
	return 'all';
};
const monthKeyword = ref(getExistingMonthQuery());
const yearList = computed(() => {
	return [...new Set([...yearMonthList.value].map((data) => data.year.toString()))];
});
const monthList = computed(() => {
	switch (yearKeyword.value) {
		case 'all': {
			return [...new Set([...yearMonthList.value].map((data) => +data.month ))].sort(function (a, b) {
				return a - b;
			});
		}
		default: {
			let months = [...new Set([...yearMonthList.value].map((data) => {
				if (data.year == yearKeyword.value) {
					return +data.month;
				}
				return null;
			} ))].sort(function (a, b) {
				return a - b;
			});
			months.shift();
			return months;
		}
	}
});
const _applyFilterToData = (data) => {
	if (yearKeyword.value == 'all' && monthKeyword.value == 'all' && brandKeyword.value == 'all' && !searchKeyword.value) {
		return data;
	}
	return data.filter((item) => {
		if (brandKeyword.value == 'all' && yearKeyword.value == 'all' && monthKeyword.value == 'all') {
			return item.title.toLowerCase().includes(searchKeyword.value.toLowerCase());
		} else if (yearKeyword.value == 'all' && monthKeyword.value == 'all' && brandKeyword.value != 'all') {
			return (
				brandKeyword.value == item.categoryTagTitle &&
				item.title.toLowerCase().includes(searchKeyword.value.toLowerCase())
			);
		} else if (yearKeyword.value != 'all' && monthKeyword.value == 'all' && brandKeyword.value != 'all') {
			return (
				brandKeyword.value == item.categoryTagTitle &&
				yearKeyword.value == item.year &&
				item.title.toLowerCase().includes(searchKeyword.value.toLowerCase())
			);
		} else if (yearKeyword.value != 'all' && monthKeyword.value == 'all' && brandKeyword.value == 'all') {
			return (
				yearKeyword.value == item.year &&
				item.title.toLowerCase().includes(searchKeyword.value.toLowerCase())
			);
		} else if (yearKeyword.value != 'all' && monthKeyword.value != 'all' && brandKeyword.value == 'all') {
			return (
				yearKeyword.value == item.year &&
				monthKeyword.value == item.month &&
				item.title.toLowerCase().includes(searchKeyword.value.toLowerCase())
			);
		} else if (yearKeyword.value == 'all' && monthKeyword.value != 'all' && brandKeyword.value == 'all') {
			return (
				monthKeyword.value == item.month &&
				item.title.toLowerCase().includes(searchKeyword.value.toLowerCase())
			);
		} else if (yearKeyword.value == 'all' && monthKeyword.value != 'all' && brandKeyword.value != 'all') {
			return (
				monthKeyword.value == item.month &&
				brandKeyword.value == item.categoryTagTitle &&
				item.title.toLowerCase().includes(searchKeyword.value.toLowerCase())
			);
		} else {
			return (
				yearKeyword.value == item.year &&
				monthKeyword.value == item.month &&
				brandKeyword.value == item.categoryTagTitle &&
				item.title.toLowerCase().includes(searchKeyword.value.toLowerCase())
			);
		}
	});
};

const _applyParamsToFilter = () => {
	let monthTranslated = dayjs().set('month', (+monthKeyword.value - 1)).format('MMMM');
	switch (yearKeyword.value) {
		case 'all': {
			if (monthKeyword.value == 'all' && brandKeyword.value == 'all') {
				router.replace({ query: { ...route.query, year: undefined, month: undefined, brand: undefined } });
			} else if (monthKeyword.value != 'all' && brandKeyword.value == 'all') {
				router.replace({ query: { ...route.query, year: undefined, month: monthTranslated, brand: undefined } });
			} else if (monthKeyword.value == 'all' && brandKeyword.value != 'all') {
				router.replace({ query: { ...route.query, year: undefined, month: undefined, brand: brandKeyword.value } });
			} else {
				router.replace({ query: { ...route.query, year: undefined, month: monthTranslated, brand: brandKeyword.value } });
			}
			break;
		}
		default: {
			if (monthKeyword.value == 'all' && brandKeyword.value == 'all') {
				router.replace({ query: { ...route.query, year: yearKeyword.value, month: undefined, brand: undefined } });
			} else if (monthKeyword.value != 'all' && brandKeyword.value == 'all') {
				router.replace({ query: { ...route.query, year: yearKeyword.value, month: monthTranslated, brand: undefined } });
			} else if (monthKeyword.value == 'all' && brandKeyword.value != 'all') {
				router.replace({ query: { ...route.query, year: yearKeyword.value, month: undefined, brand: brandKeyword.value } });
			} else {
				router.replace({ query: { ...route.query, year: yearKeyword.value, month: monthTranslated, brand: brandKeyword.value } });
			}
			break;
		}
	}
};

// ------------------------ Logic regarding paginations ------------------------
// -- data --
const PAGE_SIZE = 6;
const currentPageNumber = ref(1);
const MOBILE_PAGINATION_NUMBER_TO_SHOW = 3;
const DESKTOP_PAGINATION_NUMBER_TO_SHOW = 7;

// -- computed --
const paginationStartIndex = computed(() => {
	return (currentPageNumber.value - 1) * PAGE_SIZE;
});
const paginationEndIndex = computed(() => {
	return Math.min(searchResults.value?.length ?? 0, (paginationStartIndex.value + PAGE_SIZE));
});


// -- methods --
const _applyPaginationToData = (data) => {
	return data.slice(paginationStartIndex.value, paginationEndIndex.value);
};
const handlePaginationChange = (pageInfo) => {
	currentPageNumber.value = pageInfo.currentPage;
	MHScrollToElement(contentEl.value);
};


// ------------------------ Misc. logic ------------------------
const searchResultDisplayed = computed(() => {
	let result = [];
	if (searchResults.value.length > 0) {
		result = _applyFilterToData(searchResults.value);
	}
	return result;
});

const searchResultDisplayedPaginated = computed(() => {
	return _applyPaginationToData(searchResultDisplayed.value);
});

const numOfPaginationToShow = computed(() => {
	const pageCount = Math.max(1, Math.ceil(searchResultDisplayed.value.length / PAGE_SIZE));
	let numShown = isMobileViewport.value ? Math.min(MOBILE_PAGINATION_NUMBER_TO_SHOW, pageCount) : Math.min(DESKTOP_PAGINATION_NUMBER_TO_SHOW, pageCount);
	let first = currentPageNumber.value - Math.floor(numShown / 2);
	first = Math.max(first, 1);
	first = Math.min(first, pageCount - numShown + 1);

	return [...Array(numShown)].map((k, i) => i + first);
});

const interpolatedNumberOfResultsString = computed(() => {
	return props.numberOfResults.replace(/\{0\}/gi, `${paginationStartIndex.value + 1} to ${paginationEndIndex.value}`).replace(/\{1\}/gi, searchResultDisplayed.value.length);
});

const getSearchResults = async () => {
	let apiResponse = null;

	isLoading.value = true;
	apiErrorMsg.value = '';

	try {
		apiResponse = await axios.get(`${props.mediaCentreSearchUrl}`, { params: { show: 'all' } });
		searchResults.value = apiResponse.data.hits.filter((item) => item.year != 0);
	} catch {
		apiErrorMsg.value = 'Unable to fetch search results';
	}
	isLoading.value = false;
};

const handleFilter = (value) => {
	yearKeyword.value = value.year;
	monthKeyword.value = value.month;
	brandKeyword.value = value.brand;
	_applyParamsToFilter();
};

const getYearList = async () => {

	let apiResponse = null;
	
	isLoading.value = true;
	apiErrorMsg.value = '';
	
	try {
		apiResponse = await axios.get(`${props.mediaCentreSearchUrl}`, { params: { show: 'all' } });

		apiResponse.data.hits.forEach((item) => {
			if (item.year != 0) {
				yearMonthList.value.add({ year: item.year, month: item.month });
			}
		});
	} catch {
		apiErrorMsg.value = 'Unable to fetch search results';
	}
	isLoading.value = false;
};
// TODO: Refactor getExistingQuery() function to use useRouteHash() instead

getSearchResults();
getYearList();

const {
	currentPage: paginationCurrentPage,
	currentPageSize: paginationCurrentPageSize,
	pageCount: paginationPageCount,
	isFirstPage: paginationIsFirstPage,
	isLastPage: paginationIsLastPage,
	next: paginationNext,
	prev: paginationPrev,
} = useOffsetPagination({
	total: computed(() => searchResultDisplayed.value.length),
	page: currentPageNumber,
	pageSize: PAGE_SIZE,
	onPageChange: handlePaginationChange,
});

const { isRTL } = useIsRTL();
</script>

<template>
<template v-if="isEditorMode && props.isHideInDesktop">
	<div class="generic-container py-10 text-center">
		<p>Hidden in: <span v-if="props.isHideInDesktop">Desktop</span> <span v-if="props.isHideInMobile">, Mobile</span></p>
	</div>
</template>
<template v-else>
	<div
		v-if="((!props.isHideInDesktop && !isMobileViewport) ||
			(!props.isHideInMobile && isMobileViewport)) &&
			props.searchPagePath"
		ref="contentEl"
		class="MediaCentreSearch generic-container"
	>
		<div
			class="grid md:flex md:flex-row-reverse gap-6 mb-10 md:mb-8 grid-cols-11"
		>
			<div
				:class="{
					'col-span-4': !props.hideMonth && !props.enableBrandsDropdown || (props.enableBrandsDropdown && props.hideMonth),
					'col-span-6': !props.hideMonth && props.enableBrandsDropdown,
					'col-span-3': props.hideMonth && !props.enableBrandsDropdown,
				}"
			>
				<BrandMonthYearFilter
					:yearList="yearList"
					:monthList="monthList"
					:yearDefaultValue="yearKeyword"
					:monthDefaultValue="monthKeyword"
					:allLabel="props.allLabel"
					:resetLabel="props.mobileResetCTAText"
					:applyLabel="props.applyCTAText"
					:yearLabel="props.yearLabel"
					:monthLabel="props.monthLabel"
					:filterByLabel="props.mobileFilterByLabel"
					:hideMonth="props.hideMonth"
					:enableBrandsDropdown="props.enableBrandsDropdown"
					:brandsLabel="props.brandsLabel"
					:brandsTags="props.brandsTags"
					:brandDefaultValue="brandKeyword"
					@update:filter-query="handleFilter"
				/>
			</div>
			<div
				:class="{
					'col-span-7': !props.hideMonth && !props.enableBrandsDropdown || (props.enableBrandsDropdown && props.hideMonth),
					'col-span-5': !props.hideMonth && props.enableBrandsDropdown,
					'col-span-8': props.hideMonth && !props.enableBrandsDropdown,
				}"
			>
				<MediaCentreSearchSearch
					:searchList="searchResults"
					:monthFilter="monthKeyword"
					:yearFilter="yearKeyword"
					:searchInputPlaceholderText="props.searchPlaceHolderText"
					:searchButtonLabelText="props.searchCTAText"
					class="w-full"
					@update:search-query="searchKeyword = $event"
				/>
			</div>
		</div>
		<div v-if="isLoading" class="flex items-center justify-center h-[200px]">
			<AppSpinner />
		</div>
		<div v-else>
			<p
				v-if="(searchResultDisplayedPaginated.length > 0)"
				v-html-sanitize="interpolatedNumberOfResultsString"
				class="mb-12 md:mb-8 text-sm"
			>
			</p>

			<template v-if="(searchResultDisplayedPaginated.length > 0)">
				<div class="grid grid-cols-1 gap-y-8">
					<MediaCentreSearchListing
						v-for="(item, index) in searchResultDisplayedPaginated"
						:key="index"
						:title="item.title"
						:date="item.articleDate ? dayjs(item.articleDate).format('DD MMM YYYY') : ''"
						:category="item.categoryTagTitle"
						:description="item.articleDescription"
						:path="item.pagePath"
						:imageDesktopUrl="item.desktopArticleImage"
						:imageMobileUrl="item.mobileArticleImage"
						:rootAttrs="{
							class: '',
						}"
					/>
				</div>

				<div class="flex justify-center pt-20 font-semibold md:pt-16">
					<button
						type="button"
						:disabled="paginationIsFirstPage"
						class="pagination-icon mr-6"
						@click="paginationPrev"
					>
						<div class="flex items-center px-4">
							<icon-far-angle-left class="w-6 flex-shrink-0 mr-1 md:hidden transform rtl:-rotate-180" />
							<label>{{ props.previousButton }}</label>
						</div>
					</button>
					<div class="flex items-center gap-6">
						<button
							v-for="index in numOfPaginationToShow"
							:key="index"
							type="button"
							:disabled="currentPageNumber === index"
							:class="{
								'text-white': currentPageNumber === index,
							}"
							class="pagination-number w-8 h-8"
							@click="currentPageNumber = index"
						>
							<label>{{ index }}</label>
						</button>
					</div>
					<button
						type="button"
						:disabled="paginationIsLastPage"
						class="pagination-icon ml-6"
						@click="paginationNext"
					>
						<div class="flex items-center px-4">
							<label>{{ props.nextButton }}</label>
							<icon-far-angle-right class="w-6 flex-shrink-0 ml-1 md:hidden transform rtl:-rotate-180" />
						</div>
					</button>
				</div>
			</template>
			<template v-else>
				<div class="pt-20 pb-16 text-center">
					<p v-html-sanitize="props.noResultsMessage"></p>
				</div>
				<div class="mt-20 md:mt-10">
					<h3 class="text-center mb-10 md:mb-8">{{ props.crossSellLabel }}</h3>
					<div class="grid grid-cols-3 gap-6 md:grid-cols-1">
						<MediaCentreListingNewsTile
							v-for="(item, index) in props.featuredCrossSell"
							:key="index"
							size="small"
							:isLargeDesktop="true"
							:date="dayjs(item.articleDate).format('DD MMM YYYY')"
							:title="item.title"
							:imageDesktopUrl="item.desktopImage"
							:imageMobileUrl="item.mobileImage"
							:path="item.linkPath"
						/>
					</div>
				</div>
			</template>
		</div>

	</div>
</template>
</template>



<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.MediaCentreSearch {
	
}

.pagination-icon {
	fill: var(--primary-blue-base);
	color: var(--primary-blue-base);
	background-color: var(--neutral-grey-extralight);
	@apply px-0 h-10;

	&:hover {
		fill: var(--neutral-white-base);
		background-color: var(--primary-blue-light);
		color: var(--neutral-white-base);
	}

	&:focus {
		outline-offset: 0;
		outline: var(--semantic-blue-base);
		outline-style: solid;
		outline-width: 2px;
	}

	&:active {
		fill: var(--neutral-white-base);
		color: var(--neutral-white-base);
		background-color: var(--primary-blue-base);
	}

	&[disabled] {
		fill: var(--neutral-grey-dark);
		color: var(--neutral-grey-dark);
		background-color: transparent;
	}
}

.pagination-number {
	@apply rounded-full;

	&:hover {
		background-color: var(--neutral-grey-base);
		color: var(--primary-black-base);
		@apply bg-opacity-20;
	}

	&:focus {
		outline-offset: 0;
		outline: var(--semantic-blue-base);
		outline-style: solid;
		outline-width: 2px;
	}

	&:active, &[disabled] {
		color: var(--neutral-white-base);
		background-color: var(--primary-blue-base);
	}
}

</style>
